import React from "react";
import { Badge, Select, Switch, Text, TextInput } from "@mantine/core";
import { IconPencil } from "@tabler/icons";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import WalletMembers from "../../components/walletMembers";
import WalletShareChecker from "../../components/walletShareChecker";
import WalletPayrollBids from "../../components/walletPayrollBids";
import WalletForecastBids from "../../components/walletForecastBids";
import RemoteSelect2 from "../../components/remoteSelect2";
import { useCellRender } from "../../hooks/useCellRender";
import {
  formatWalletType,
  getWalletMemberUsers,
  getWalletOwner,
  isGroupWallet,
  WALLET_ACTIVE_FILTER_VALUES,
  WALLET_COMPANY_FILTER_VALUES,
  WALLET_TYPE_FILTER_VALUES,
} from "../../helpers/walletDisplay";

const apiEntity = "wallet";

const USER_FILTER_POPULATE = {
  model: "User",
  select: "name cname staffId aliasName",
  labelField: "name",
  sort: { name: 1 },
};

const WalletOwnerField = ({ form, label }) => {
  const [cellRender] = useCellRender();
  const owner = getWalletOwner(form.values);
  const content = !owner ? (
    <Text size="sm">-</Text>
  ) : owner.kind === "user" ? (
    cellRender.User(owner.value)
  ) : (
    <Text size="sm">{owner.value?.name || owner.value?.code || "-"}</Text>
  );

  return (
    <>
      {label && (
        <Text size="sm" weight={500} mb={4}>
          {label}
        </Text>
      )}
      {content}
    </>
  );
};

const YesNoBadge = (value) => (
  <Badge size="xs" color={value ? "green" : "gray"}>
    {value ? "Yes" : "No"}
  </Badge>
);

const WalletList = () => {
  const [cellRender] = useCellRender();

  const tableSchema = {
    showActionColumn: true,
    searchableFields: ["code", "name", "type", "status"],
    columns: [
      {
        field: "code",
        headerName: "Code",
        sortable: true,
      },
      {
        field: "name",
        headerName: "Name",
        sortable: true,
      },
      {
        field: "type",
        headerName: "Type",
        sortable: true,
        cellRender: (col, data) => formatWalletType(data.type),
        filterable: true,
        filter: {
          field: "type",
          label: "Type",
          staticValues: WALLET_TYPE_FILTER_VALUES,
          width: 220,
          height: 200,
        },
      },
      {
        field: "user",
        headerName: "Owner",
        sortable: true,
        cellRender: (col, data) => {
          const owner = getWalletOwner(data);
          if (!owner) return "-";
          if (owner.kind === "user") return cellRender.User(owner.value);
          return owner.value?.name || owner.value?.code || "-";
        },
      },
      {
        field: "members",
        headerName: "Members",
        cellRender: (col, data) =>
          cellRender.Users(getWalletMemberUsers(data)),
      },
      {
        field: "active",
        headerName: "Active",
        sortable: true,
        cellRender: (col, data) => YesNoBadge(data.active),
        filterable: true,
        filter: {
          field: "active",
          label: "Active",
          staticValues: WALLET_ACTIVE_FILTER_VALUES,
          width: 220,
          height: 200,
        },
      },
      {
        field: "isCompany",
        headerName: "Company",
        sortable: true,
        cellRender: (col, data) => YesNoBadge(data.isCompany),
        filterable: true,
        filter: {
          field: "isCompany",
          label: "Company",
          staticValues: WALLET_COMPANY_FILTER_VALUES,
          width: 220,
          height: 200,
        },
      },
      {
        field: "_filterUser",
        headerName: "User",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity,
          field: "user",
          label: "User",
          width: 300,
          height: 320,
          populate: USER_FILTER_POPULATE,
        },
      },
      {
        field: "_filterUserGroup",
        headerName: "User Group",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity,
          field: "userGroup",
          label: "User Group",
          width: 300,
          height: 320,
          populate: {
            model: "UserGroup",
            select: "name code",
            labelField: "name",
            sort: { name: 1 },
          },
        },
      },
      {
        field: "_filterMember",
        headerName: "Member",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity,
          field: "members.user",
          label: "Member",
          width: 300,
          height: 320,
          populate: USER_FILTER_POPULATE,
        },
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {},
          },
        ],
      },
    ],
  };

  const formSchema = {
    title: "Wallet",
    titleField: "code",
    apiEntity,
    display: {
      mode: ENUM_FORM_DISPLAY.LINK,
      size: "xl",
    },
    initialValues: {
      members: [],
      active: true,
    },
    layout: {
      containers: [
        {
          key: "info-section",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Wallet Info",
            titleOrder: 5,
          },
        },
        {
          key: "info-g",
          parent: "info-section",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            { key: "info-g1", props: { span: 6 } },
            { key: "info-g2", props: { span: 6 } },
          ],
        },
        {
          key: "members-section",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (values) => isGroupWallet(values),
          props: {
            title: "Members",
            titleOrder: 5,
          },
        },
        {
          key: "members-g",
          parent: "members-section",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            { key: "members-g1", props: { span: 8 } },
            { key: "members-g2", props: { span: 4 } },
          ],
        },
        {
          key: "bids-tabs",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            defaultValue: "Payroll Bids",
          },
          tab: [
            { key: "tab-payroll-bids", label: "Payroll Bids" },
            { key: "tab-forecast", label: "Forecast" },
          ],
        },
      ],
      fields: [
        {
          name: "code",
          component: TextInput,
          parent: "info-g1",
          props: {
            label: "Code",
            disabled: true,
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "info-g1",
          props: {
            label: "Name",
          },
        },
        {
          name: "type",
          component: Select,
          parent: "info-g2",
          props: {
            label: "Type",
            data: WALLET_TYPE_FILTER_VALUES,
            searchable: false,
          },
        },
        {
          name: "active",
          component: Switch,
          parent: "info-g2",
          props: {
            label: "Active",
            type: "checkbox",
            mt: "xl",
          },
        },
        {
          name: "_owner",
          component: WalletOwnerField,
          parent: "info-g1",
          visibleOnly: (values) => !isGroupWallet(values),
          props: {
            label: "Owner",
          },
        },
        {
          name: "userGroup",
          component: RemoteSelect2,
          parent: "info-g1",
          visibleOnly: (values) => isGroupWallet(values),
          props: {
            label: "User Group",
            apiEntity: "userGroup",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name", "code"],
            clearable: true,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },
        {
          name: "members",
          component: WalletMembers,
          parent: "members-g1",
          props: {
            label: "Members",
          },
        },
        {
          name: "members",
          component: WalletShareChecker,
          parent: "members-g2",
        },
        {
          name: "payrollBids",
          component: WalletPayrollBids,
          parent: "tab-payroll-bids",
        },
        {
          name: "forecastBids",
          component: WalletForecastBids,
          parent: "tab-forecast",
        },
      ],
    },
  };

  return (
    <PageList
      title="Wallets"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      showAddButton={false}
      initSort={{ by: "code", order: "asc" }}
    />
  );
};

export default WalletList;
